import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../utils/supabaseClient';
import ModernCard, { ModernCardHeader, ModernCardBody } from '../Common/ModernCard';
import { useQueryClient } from '@tanstack/react-query';
import { showToast } from '../Common/Toast';
import { formatWallClockDate } from '../../utils/dateUtils';
import { timeOffDays } from '../../utils/timeOffValidation';
import { getSchedulingSettings } from '../../utils/smartScheduling';

import { companyDateKey } from '../../utils/dateFormatters';
const TimeOffPanel = ({ companyId }) => {
  // The Time Off summary tile on the Employees page is a React Query with a 5-minute
  // staleTime (useBlockingTimeOffCount); loadData() only refreshes this list, so the tile
  // kept the old count after a save or delete (board #5017). Same key as the hook.
  const queryClient = useQueryClient();
  // Also the Employees page's "off today" markers: since ruling 1 that page does not refetch on tab focus.
  const refreshTimeOffTile = () => {
    queryClient.invalidateQueries({ queryKey: ['employee-time-off-blocking', companyId] });
    queryClient.invalidateQueries({ queryKey: ['employee-time-off-today', companyId] });
  };
  // Board #456: the owner's "Time Off Added" notification links here with ?id=<entry>, so the
  // entry it is about is the one highlighted instead of leaving them to find it in the list.
  const [focusId] = useState(() => new URLSearchParams(window.location.search).get('id'));
  const [timeOffEntries, setTimeOffEntries] = useState([]);
  const scrolledToFocus = useRef(false);
  useEffect(() => {
    if (!focusId || scrolledToFocus.current || !timeOffEntries.some((e) => e.id === focusId)) return;
    scrolledToFocus.current = true;
    document.querySelector(`[data-time-off-id="${focusId}"]`)?.scrollIntoView({ block: 'center' });
  }, [focusId, timeOffEntries]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  
  const [formData, setFormData] = useState({
    employee_id: '',
    starts_at: '',
    ends_at: '',
    kind: 'VACATION',
    note: ''
  });

  useEffect(() => {
    if (companyId) {
      loadData();
    }
  }, [companyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load employees for dropdown
      const { data: employeesData } = await supabase
        .from('employees')
        .select('id, first_name, last_name')
        .eq('company_id', companyId)
        .order('first_name');
      
      setEmployees(employeesData || []);
      
      // Load upcoming time-off entries (including today)
      const { data: timeOffData } = await supabase
        .from('employee_time_off')
        .select('*')
        .eq('company_id', companyId)
        // Time off is stored as naive wall clock parked at UTC (see formatWallClockDate),
        // so "today" is the company's calendar day, not the UTC one.
        .gte('ends_at', companyDateKey())
        .order('starts_at');
      
      // Enrich with employee names
      const enrichedData = (timeOffData || []).map(entry => {
        const employee = employeesData?.find(e => e.id === entry.employee_id);
        return {
          ...entry,
          employee_name: employee ? `${employee.first_name} ${employee.last_name}` : 'Unknown'
        };
      });
      
      setTimeOffEntries(enrichedData);
    } catch (error) {
      console.error('Error loading time-off data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.employee_id || !formData.starts_at || !formData.ends_at) {
      showToast('error', 'Please fill in all required fields');
      return;
    }

    const [startYear, startMonth, startDay] = formData.starts_at.split('-').map(Number);
    const [endYear, endMonth, endDay] = formData.ends_at.split('-').map(Number);
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);

    // Basic validation - just ensure end is not before start
    if (end < start) {
      showToast('error', 'End date cannot be before start date');
      return;
    }

    setSubmitting(true);

    try {
      // #5324: ONE day-count rule shared with Timesheets — company open days (business_hours), 8h each.
      const { business_hours: businessHours } = await getSchedulingSettings(companyId);
      const hoursRequested = timeOffDays(formData.starts_at, formData.ends_at, { businessHours }) * 8;
      const { error } = await supabase
        .from('employee_time_off')
        .insert({
          employee_id: formData.employee_id,
          company_id: companyId,
          kind: formData.kind,
          // starts_at is the START of the first day; ends_at must be the END of the last one.
          //
          // Both used to be written as the bare `<input type="date">` string, which Postgres
          // stores as UTC midnight — so a one-day request came out with starts_at EQUAL to
          // ends_at. `compute_available_time_slots` (and `get_available_time_slots_consolidated`)
          // test overlap as `eto.starts_at < s.slot_end AND eto.ends_at > s.slot_start`, and a
          // zero-length interval overlaps nothing. Every single-day time-off request therefore
          // blocked no slots at all, and a multi-day one silently left its final day bookable —
          // the employee stayed offerable to customers on days they had booked off. Two of the
          // three rows live on 2026-09-06 are in exactly that state.
          //
          // The `T00:00:00` / `T23:59:59` pair is not invented here — it is the convention
          // `PTOEditModal.js:49-54` already writes for a full day, and the one it reads back to
          // decide `isFullDay` (`:26-27`, hours 0/0 and 23/59). This form only collects dates, so
          // every entry it makes is a full day. Matching the format exactly is what keeps a
          // request created here editable in that modal.
          //
          // 23:59:59 rather than the next day's midnight because these are WALL-CLOCK values read
          // back at UTC (see formatWallClockDate below): an exclusive end would render the day
          // AFTER the last day off.
          starts_at: `${formData.starts_at}T00:00:00`,
          ends_at: `${formData.ends_at}T23:59:59`,
          hours_requested: hoursRequested,
          note: formData.note || null,
          status: 'PENDING'
        });

      if (error) throw error;

      showToast('success', 'Time off added successfully');
      setShowForm(false);
      setFormData({
        employee_id: '',
        starts_at: '',
        ends_at: '',
        kind: 'VACATION',
        note: ''
      });
      loadData();
      refreshTimeOffTile();
    } catch (error) {
      console.error('Error adding time off:', error);
      console.error('Error details:', error?.details || error?.message || JSON.stringify(error));
      showToast('error', error?.message || 'Failed to add time off');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this time-off entry?')) return;
    
    try {
      const { error } = await supabase
        .from('employee_time_off')
        .delete()
        .eq('id', id);

      if (error) throw error;

      showToast('success', 'Time off deleted');
      loadData();
      refreshTimeOffTile();
    } catch (error) {
      console.error('Error deleting time off:', error);
      showToast('error', 'Failed to delete time off');
    }
  };

  // These are WALL-CLOCK values (naive local, stored as UTC) — see formatWallClockDate.
  // formatDate() is wrong here and reading it as browser-local is what displayed every
  // request a day early. Do not "simplify" this back to the generic formatter; that swap is
  // exactly what regressed it last time (b392b8440).
  const formatDate = (dateStr) => formatWallClockDate(dateStr);

  // KEYED UPPERCASE, matching time_off_kind_enum (verified live 2026-09-19: VACATION, SICK,
  // PERSONAL, BEREAVEMENT, JURY_DUTY, OTHER, HOLIDAY, UNPAID, FMLA, JURY). These maps were
  // keyed lowercase while the rows coming back are uppercase, so every existing entry fell
  // through to `|| kind` and rendered the raw enum name in the generic grey pill — the
  // display half of the same casing bug as the failing insert (board #940).
  const getKindLabel = (kind) => {
    const labels = {
      VACATION: 'Vacation',
      SICK: 'Sick',
      PERSONAL: 'Personal',
      BEREAVEMENT: 'Bereavement',
      JURY_DUTY: 'Jury Duty',
      JURY: 'Jury Duty',
      HOLIDAY: 'Holiday',
      UNPAID: 'Unpaid',
      FMLA: 'FMLA',
      OTHER: 'Other'
    };
    return labels[kind] || kind;
  };

  const getKindColor = (kind) => {
    const colors = {
      VACATION: 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-300',
      SICK: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300',
      PERSONAL: 'bg-purple-100 text-purple-800 dark:bg-purple-900/30 dark:text-purple-300',
      BEREAVEMENT: 'bg-slate-100 text-slate-800 dark:bg-slate-900/30 dark:text-slate-300',
      JURY_DUTY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      JURY: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
      HOLIDAY: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300',
      UNPAID: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-300',
      FMLA: 'bg-teal-100 text-teal-800 dark:bg-teal-900/30 dark:text-teal-300',
      OTHER: 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-300'
    };
    return colors[kind] || colors.OTHER;
  };

  if (loading) {
    return (
      <ModernCard>
        <ModernCardHeader title="Time Off" gradient="blue" />
        <ModernCardBody>
          <div className="animate-pulse space-y-3">
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
            <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded"></div>
          </div>
        </ModernCardBody>
      </ModernCard>
    );
  }

  return (
    <div className="space-y-4">
      <ModernCard>
        <ModernCardHeader 
          title="Time Off" 
          gradient="blue"
          action={
            <button
              onClick={() => setShowForm(!showForm)}
              className="px-3 py-1.5 text-sm bg-white dark:bg-slate-800 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors font-medium"
            >
              {showForm ? 'Cancel' : '+ Add Time Off'}
            </button>
          }
        />
        <ModernCardBody>
          {showForm && (
            <form onSubmit={handleSubmit} className="mb-6 p-4 bg-gray-50 dark:bg-slate-800 rounded-lg space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Employee *
                  </label>
                  <select
                    value={formData.employee_id}
                    onChange={(e) => setFormData({ ...formData, employee_id: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  >
                    <option value="">Select employee...</option>
                    {employees.map(emp => (
                      <option key={emp.id} value={emp.id}>
                        {emp.first_name} {emp.last_name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Type *
                  </label>
                  <select
                    value={formData.kind}
                    onChange={(e) => setFormData({ ...formData, kind: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {/* UPPERCASE values — time_off_kind_enum is uppercase (verified live).
                        These were lowercase while the initial state was already 'VACATION',
                        so the untouched default saved fine and CHOOSING a type was what
                        broke it: the insert sent 'sick' against an enum holding 'SICK'
                        (board #940). HOLIDAY is deliberately absent — operator ruling
                        2026-09-18: holidays are system-managed, not user-created. */}
                    <option value="VACATION">Vacation</option>
                    <option value="SICK">Sick</option>
                    <option value="PERSONAL">Personal</option>
                    <option value="BEREAVEMENT">Bereavement</option>
                    <option value="JURY_DUTY">Jury Duty</option>
                    <option value="UNPAID">Unpaid</option>
                    <option value="OTHER">Other</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    value={formData.starts_at}
                    onChange={(e) => setFormData({ ...formData, starts_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    value={formData.ends_at}
                    onChange={(e) => setFormData({ ...formData, ends_at: e.target.value })}
                    className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                    required
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Note (optional)
                </label>
                <textarea
                  value={formData.note}
                  onChange={(e) => setFormData({ ...formData, note: e.target.value })}
                  rows={2}
                  className="w-full px-3 py-2 rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-slate-900 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-primary-500"
                  placeholder="Any additional notes..."
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 font-medium"
              >
                {submitting ? 'Saving...' : 'Save Time Off'}
              </button>
            </form>
          )}

          {/* Time Off Entries Table */}
          {timeOffEntries.length === 0 ? (
            <p className="text-gray-500 dark:text-gray-400 text-center py-8">
              No upcoming time off scheduled
            </p>
          ) : (
            <div className="table-scroll">
              <table className="w-full">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    <th className="pb-3">Employee</th>
                    <th className="pb-3">Type</th>
                    <th className="pb-3">Start Date</th>
                    <th className="pb-3">End Date</th>
                    <th className="pb-3">Note</th>
                    <th className="pb-3">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {timeOffEntries.map((entry) => (
                    <tr
                      key={entry.id}
                      data-time-off-id={entry.id}
                      className={`hover:bg-gray-50 dark:hover:bg-slate-800/50 ${entry.id === focusId ? 'bg-blue-50 dark:bg-blue-900/30 ring-2 ring-inset ring-blue-400' : ''}`}
                    >
                      <td className="py-3 text-gray-900 dark:text-white font-medium">
                        {entry.employee_name}
                      </td>
                      <td className="py-3">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getKindColor(entry.kind)}`}>
                          {getKindLabel(entry.kind)}
                        </span>
                      </td>
                      <td className="py-3 text-gray-600 dark:text-gray-300">
                        {formatDate(entry.starts_at)}
                      </td>
                      <td className="py-3 text-gray-600 dark:text-gray-300">
                        {formatDate(entry.ends_at)}
                      </td>
                      <td className="py-3 text-gray-500 dark:text-gray-400 text-sm">
                        {entry.note || '-'}
                      </td>
                      <td className="py-3">
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300 text-sm font-medium"
                        >
                          Delete
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </ModernCardBody>
      </ModernCard>
    </div>
  );
};

export default TimeOffPanel;
